import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import data from "layouts/dashboard/components/Projects/data";

function Processors() {
  const { t } = useTranslation();
  const { processorTableColumns } = data();
  const [rawProcessors, setRawProcessors] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [updatingProcessorType, setUpdatingProcessorType] = useState("");
  const api = useApi();

  // Filter state
  const [filterProcessorType, setFilterProcessorType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterCurrency, setFilterCurrency] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const categoryOptions = useMemo(() => {
    return [...new Set(rawProcessors.map((p) => p.processorCategory).filter(Boolean))].sort();
  }, [rawProcessors]);

  const normalizeProcessors = (processorRows) =>
    processorRows.map((item) => ({
      ...item,
      action: (
        <MDButton
          variant="contained"
          size="small"
          color={item.active ? "error" : "success"}
          disabled={updatingProcessorType === item.processorType}
          onClick={() => {
            setSelectedProcessor(item);
            setOpenConfirmDialog(true);
          }}
        >
          {item.active ? t("processorPage.disable") : t("processorPage.enable")}
        </MDButton>
      ),
    }));

  const getProcessors = () => {
    api
      .get("processors")
      .then((response) => {
        const processorRows = Array.isArray(response.data?.body)
          ? response.data.body
          : Array.isArray(response.data)
            ? response.data
            : [];
        setRawProcessors(processorRows);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error(t("no_permission_message"), {
            id: "ProcessorsPermissionError",
          });
          return;
        }
        toast.error(t("processorPage.failedToLoad"), { id: "ProcessorsFetchError" });
        console.error("Error fetching processors:", error);
      });
  };

  useEffect(() => {
    let result = [...rawProcessors];
    if (filterProcessorType.trim()) {
      result = result.filter((p) => p.processorType?.toLowerCase().includes(filterProcessorType.trim().toLowerCase()));
    }
    if (filterCategory) {
      result = result.filter((p) => p.processorCategory === filterCategory);
    }
    if (filterCurrency.trim()) {
      result = result.filter((p) => {
        const currencies = p.supportedCurrencies;
        const currencyText = Array.isArray(currencies) ? currencies.join(",") : currencies;
        return currencyText?.toLowerCase().includes(filterCurrency.trim().toLowerCase());
      });
    }
    if (filterStatus !== "") {
      result = result.filter((p) => String(!!p.active) === filterStatus);
    }
    setProcessors(normalizeProcessors(result));
  }, [rawProcessors, filterProcessorType, filterCategory, filterCurrency, filterStatus, updatingProcessorType]);

  const resetFilters = () => {
    setFilterProcessorType("");
    setFilterCategory("");
    setFilterCurrency("");
    setFilterStatus("");
  };

  const handleConfirmDialogClose = () => {
    setOpenConfirmDialog(false);
    setSelectedProcessor(null);
  };

  const updateProcessorStatus = () => {
    if (!selectedProcessor?.processorType) {
      handleConfirmDialogClose();
      return;
    }

    const nextActiveState = !selectedProcessor.active;
    const encodedProcessorType = encodeURIComponent(selectedProcessor.processorType);

    setUpdatingProcessorType(selectedProcessor.processorType);

    api
      .patch(`processors/${encodedProcessorType}/status`, { active: nextActiveState })
      .then(() => {
        toast.success(
          t(nextActiveState ? "processorPage.enabledSuccess" : "processorPage.disabledSuccess", {
            processorType: selectedProcessor.processorType,
          }),
          { id: "ProcessorStatusUpdateSuccess" }
        );
        getProcessors();
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error(t("no_permission_message"), {
            id: "ProcessorsStatusPermissionError",
          });
        } else {
          toast.error(t("processorPage.failedToUpdate"), {
            id: "ProcessorsStatusUpdateError",
          });
        }
        console.error("Error updating processor status:", error);
      })
      .finally(() => {
        setUpdatingProcessorType("");
        handleConfirmDialogClose();
      });
  };

  useEffect(() => {
    getProcessors();
  }, []);

  return (
    <DashboardLayout>
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <MDTypography variant="h5" fontWeight="medium">
              {t("processorPage.title")}
            </MDTypography>
            <MDTypography variant="button" fontWeight="regular" color="secondary">
              {processors.length} {t("processorPage.title")}
            </MDTypography>
          </Grid>
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label={t("processorTableColumns.processorType")}
                    value={filterProcessorType}
                    onChange={(e) => setFilterProcessorType(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label={t("processorTableColumns.category")}
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "38px" },
                      "& .MuiSelect-select": { fontSize: "0.8rem" },
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t("all")}</MenuItem>
                    {categoryOptions.map((category) => (
                      <MenuItem key={category} value={category} style={{ fontSize: "0.8rem" }}>{category}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label={t("processorTableColumns.currencies")}
                    value={filterCurrency}
                    onChange={(e) => setFilterCurrency(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label={t("processorTableColumns.status")}
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "38px" },
                      "& .MuiSelect-select": { fontSize: "0.8rem" },
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t("all")}</MenuItem>
                    <MenuItem value="true" style={{ fontSize: "0.8rem" }}>{t("active")}</MenuItem>
                    <MenuItem value="false" style={{ fontSize: "0.8rem" }}>{t("inactive")}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg="auto">
                  <MDButton variant="outlined" color="secondary" onClick={resetFilters} size="small" style={{ fontSize: "0.75rem", padding: "5px 16px", whiteSpace: "nowrap" }}>
                    {t("reset")}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
              <DataTable
                table={{ columns: processorTableColumns, rows: processors }}
                isSorted={true}
                showTotalEntries={true}
                entriesPerPage={true}
                entriesPerPagePosition="bottom"
                noEndBorder
                canSearch={false}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={openConfirmDialog} onClose={handleConfirmDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("processorPage.confirmAction")}</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">
            {selectedProcessor?.active
              ? t("processorPage.disableProcessor")
              : t("processorPage.enableProcessor")}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleConfirmDialogClose}>
            {t("cancel")}
          </MDButton>
          <MDButton
            variant="contained"
            color={selectedProcessor?.active ? "error" : "success"}
            onClick={updateProcessorStatus}
            disabled={!selectedProcessor || updatingProcessorType === selectedProcessor.processorType}
          >
            {selectedProcessor?.active ? t("processorPage.disable") : t("processorPage.enable")}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Processors;
